import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  useTheme,
  Paper,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Button,
  Grid,
  Tabs,
  Tab,
  Skeleton,
  alpha,
  Container,
  Tooltip,
  Badge,
  Stack,
} from "@mui/material";

import TopAppBar from "../../global/TopBar";
import SideBar from "../../global/SideBar";
import { tokens } from "../../../theme";
import { useAuth } from "../../../AuthContext";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CakeIcon from "@mui/icons-material/Cake";
import BadgeIcon from "@mui/icons-material/Badge";
import FlagIcon from "@mui/icons-material/Flag";
import FavoriteIcon from "@mui/icons-material/Favorite";
import VerifiedIcon from "@mui/icons-material/Verified";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LoginIcon from "@mui/icons-material/Login";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MessageIcon from "@mui/icons-material/Message";
import TodayIcon from "@mui/icons-material/Today";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import TimelineIcon from "@mui/icons-material/Timeline";
import SecurityIcon from "@mui/icons-material/Security";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SearchOffIcon from "@mui/icons-material/SearchOff";

const IMAGE_BASE = "https://mindsinaction.com.na/assets/media/pictures/";

const LEAVE_STATUS_LABEL = {
  0: "Pending",
  1: "In progress",
  2: "Approved",
  3: "Rejected",
  4: "Returned",
};

const MANAGER_ROLES = ["admin", "hr", "ceo", "coo", "supperAdmin"];

/* ─── Helpers ──────────────────────────────────────────────────────────── */

const parseField = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return value.length ? value : null;
  if (typeof value === "object") return [value];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.length ? parsed : null;
      if (parsed && typeof parsed === "object") return [parsed];
      return null;
    } catch {
      // Plain string that was never JSON — treat it as a single display value.
      return [{ _raw: value }];
    }
  }
  return null;
};

const isFlagged = (v) => v === "1" || v === 1 || v === true;

const isActiveStaff = (s) =>
  s?.status === "active" || s?.status === 1 || s?.status === "1";

const sameId = (a, b) => String(a) === String(b);

const formatDate = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const dobText = (staff) => {
  const dob = parseField(staff?.date_of_birth)?.[0];
  if (!dob) return "N/A";
  if (dob._raw) return dob._raw;
  const { day, month, year } = dob;
  if (!day && !month && !year) return "N/A";
  return `${day || "?"}/${month || "?"}/${year || "?"}`;
};

const addressText = (raw, keys) => {
  const parsed = parseField(raw)?.[0];
  if (!parsed) return "N/A";
  if (parsed._raw) return parsed._raw;
  const parts = keys.map((k) => parsed[k]).filter(Boolean);
  return parts.length ? parts.join(", ") : "N/A";
};

/* ─── Tab panel ────────────────────────────────────────────────────────── */

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`staff-tabpanel-${index}`}
      aria-labelledby={`staff-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

const StaffProfile = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { id } = useParams();
  const navigate = useNavigate();
  const { getData, userRole } = useAuth();

  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [staffLeaves, setStaffLeaves] = useState([]);
  const [staffBorrows, setStaffBorrows] = useState([]);

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      pageBg: isDark ? colors.dark[800] : "#e9ebef",
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  const leaveStatusColor = useCallback(
    (s) =>
      ({
        0: colors.orange[500],
        1: colors.newTeal[500],
        2: colors.green[500],
        3: colors.red[500],
        4: colors.indigo[500],
      }[Number(s)] || colors.gray[500]),
    [colors]
  );

  /* ---------------- Data ---------------- */
  useEffect(() => {
    let cancelled = false;

    async function fetchStaffData() {
      setLoading(true);
      try {
        const [staffRes, leavesRes, borrowRes] = await Promise.all([
          getData("proccess.php", "staffs"),
          getData("proccess.php", "leaves_V2"),
          getData("proccess.php", "borrow_V2"),
        ]);

        if (cancelled) return;

        if (staffRes.success) {
          setStaff(staffRes.data?.find((s) => sameId(s.Id, id)) || null);
        }
        if (leavesRes.success) {
          setStaffLeaves(
            (leavesRes.data || []).filter((l) => sameId(l.staff_id, id))
          );
        }
        if (borrowRes.success) {
          setStaffBorrows(
            (borrowRes.data || []).filter((b) => sameId(b.staff_id, id))
          );
        }
      } catch (err) {
        console.error("Error fetching staff data:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchStaffData();
    return () => {
      cancelled = true;
    };
  }, [id, getData]);

  /* ---------------- Derived values ---------------- */
  const active = isActiveStaff(staff);
  const statusColor = active ? colors.green[500] : colors.red[500];
  const statusText = active ? "Active" : "Inactive";

  const initials = staff
    ? `${staff.FirstName?.[0] || ""}${staff.LastName?.[0] || ""}`.toUpperCase()
    : "";

  const daysEmployed = useMemo(() => {
    if (!staff?.createDate) return 0;
    const start = new Date(staff.createDate);
    if (Number.isNaN(start.getTime())) return 0;
    return Math.max(0, Math.floor((Date.now() - start.getTime()) / 86400000));
  }, [staff]);

  const nextOfKin = useMemo(() => parseField(staff?.next_of_kin)?.[0] || null, [staff]);

  /**
   * Activity is derived from real records only — account creation, last login,
   * verification, leave requests and borrow records. No synthetic events.
   */
  const staffActivity = useMemo(() => {
    const items = [];

    if (staff?.createDate)
      items.push({
        key: "created",
        type: "account",
        action: "Joined the organisation",
        date: staff.createDate,
      });

    if (staff?.verified_at)
      items.push({
        key: "verified",
        type: "verify",
        action: "Account verified",
        date: staff.verified_at,
      });

    if (staff?.last_login)
      items.push({
        key: "login",
        type: "auth",
        action: "Last signed in",
        date: staff.last_login,
      });

    staffLeaves.forEach((l) =>
      items.push({
        key: `leave-${l.id}`,
        type: "leave",
        action: `Requested ${l.leave_type || ""} leave`.trim(),
        meta: LEAVE_STATUS_LABEL[Number(l.status)],
        date: l.startDate,
      })
    );

    staffBorrows.forEach((b) =>
      items.push({
        key: `borrow-${b.id}`,
        type: "borrow",
        action: `Borrowed ${b.equipment || "an item"}`,
        meta: isFlagged(b.status) ? "Returned" : "Outstanding",
        date: b.date,
      })
    );

    return items
      .filter((i) => i.date && !Number.isNaN(new Date(i.date).getTime()))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [staff, staffLeaves, staffBorrows]);

  const statCards = useMemo(
    () => [
      {
        label: "Total leaves",
        value: staffLeaves.length,
        icon: <EventAvailableIcon />,
        color: colors.orange[500],
      },
      {
        label: "Pending leaves",
        value: staffLeaves.filter((l) => Number(l.status) === 0).length,
        icon: <ScheduleIcon />,
        color: colors.yellow[500],
      },
      {
        label: "Borrowed items",
        value: staffBorrows.length,
        icon: <AssignmentIcon />,
        color: colors.newTeal[500],
      },
      {
        label: "Days employed",
        value: daysEmployed,
        icon: <TimelineIcon />,
        color: colors.green[500],
      },
    ],
    [staffLeaves, staffBorrows, daysEmployed, colors]
  );

  /* ---------------- Shared bits ---------------- */
  const InfoItem = ({ icon, color, label, value, verified }) => (
    <ListItem disableGutters sx={{ px: 1, py: 1.25 }}>
      <ListItemAvatar>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: alpha(color, isDark ? 0.16 : 0.12),
            color,
          }}
        >
          {icon}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        disableTypography
        primary={
          <Typography
            variant="h6"
            sx={{
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: ui.textSecondary,
            }}
          >
            {label}
          </Typography>
        }
        secondary={
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: ui.textPrimary, wordBreak: "break-word" }}
            >
              {value || "N/A"}
            </Typography>
            {verified && (
              <Tooltip title="Verified">
                <VerifiedIcon sx={{ fontSize: 16, color: colors.green[500] }} />
              </Tooltip>
            )}
          </Stack>
        }
      />
    </ListItem>
  );

  const NarrativeCard = ({ title, text }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 2.5,
        bgcolor: alpha(ui.surfaceAlt, isDark ? 0.55 : 0.75),
        border: `1px solid ${ui.border}`,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 0.85,
          letterSpacing: 0.7,
          textTransform: "uppercase",
          fontWeight: 700,
          color: ui.accent,
        }}
      >
        {title}
      </Typography>
      <Typography variant="h5" sx={{ lineHeight: 1.6, color: ui.textPrimary }}>
        {text || "N/A"}
      </Typography>
    </Paper>
  );

  const EmptyState = ({ icon, text }) => (
    <Stack alignItems="center" spacing={1.25} sx={{ py: 6 }}>
      <Box sx={{ color: ui.textSecondary, opacity: 0.6, fontSize: 36, display: "flex" }}>
        {icon}
      </Box>
      <Typography variant="h6" sx={{ color: ui.textSecondary }}>
        {text}
      </Typography>
    </Stack>
  );

  const shellSx = { display: "flex", height: "100vh", bgcolor: ui.pageBg };

  /* ---------------- Loading ---------------- */
  if (loading) {
    return (
      <Box sx={shellSx}>
        <SideBar />
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <TopAppBar />
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <Container maxWidth="xl" sx={{ py: 3.5 }}>
              <Skeleton
                variant="rounded"
                height={188}
                sx={{ borderRadius: 3, mb: 3, bgcolor: alpha(ui.textSecondary, 0.12) }}
              />
              <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                    <Skeleton
                      variant="rounded"
                      height={108}
                      sx={{ borderRadius: 3, bgcolor: alpha(ui.textSecondary, 0.12) }}
                    />
                  </Grid>
                ))}
              </Grid>
              <Skeleton
                variant="rounded"
                height={380}
                sx={{ borderRadius: 3, bgcolor: alpha(ui.textSecondary, 0.12) }}
              />
            </Container>
          </Box>
        </Box>
      </Box>
    );
  }

  /* ---------------- Not found ---------------- */
  if (!staff) {
    return (
      <Box sx={shellSx}>
        <SideBar />
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <TopAppBar />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack alignItems="center" spacing={2}>
              <SearchOffIcon sx={{ fontSize: 54, color: ui.textSecondary, opacity: 0.6 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, color: ui.textPrimary }}>
                Staff member not found
              </Typography>
              <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                No record matches ID {id}.
              </Typography>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  mt: 1,
                  px: 2.75,
                  py: 1.1,
                  borderRadius: 2,
                  fontWeight: 700,
                  textTransform: "none",
                  color: "#ffffff",
                  background: ui.gradient,
                }}
              >
                Go back
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  }

  const canDelete = MANAGER_ROLES.includes(userRole);

  const actionBtn = (color) => ({
    borderRadius: 2,
    color,
    border: `1px solid ${alpha(color, 0.4)}`,
    "&:hover": { bgcolor: alpha(color, 0.12), borderColor: color },
    "&.Mui-disabled": { color: alpha(color, 0.35), borderColor: alpha(color, 0.15) },
  });

  /* ------------------------------------------------------------------ */
  return (
    <Box sx={shellSx}>
      <SideBar />

      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
        <TopAppBar />

        <Box sx={{ flex: 1, overflow: "auto" }}>
          <Container maxWidth="xl" sx={{ py: 3.5 }}>
            {/* ---------------------------------------------------------- */}
            {/* Back                                                        */}
            {/* ---------------------------------------------------------- */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  borderRadius: 2,
                  color: ui.textSecondary,
                  bgcolor: ui.surface,
                  border: `1px solid ${ui.border}`,
                  "&:hover": {
                    color: ui.accent,
                    borderColor: alpha(ui.accent, 0.5),
                    bgcolor: alpha(ui.accent, 0.08),
                  },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Box>
                <Typography
                  variant="h2"
                  sx={{ fontWeight: 800, letterSpacing: -0.4, color: ui.textPrimary }}
                >
                  Staff Profile
                </Typography>
                <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                  Viewing details for {staff.FirstName} {staff.LastName}
                </Typography>
              </Box>
            </Stack>

            {/* ---------------------------------------------------------- */}
            {/* Profile header                                              */}
            {/* ---------------------------------------------------------- */}
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: ui.surface,
                border: `1px solid ${ui.border}`,
                boxShadow: ui.shadow,
              }}
            >
              <Box
                sx={{
                  height: 92,
                  background: `linear-gradient(135deg, ${alpha(
                    ui.accent,
                    isDark ? 0.32 : 0.24
                  )} 0%, ${alpha(ui.accent2, isDark ? 0.2 : 0.14)} 100%)`,
                }}
              />

              <Box sx={{ p: { xs: 2.5, md: 3 }, pt: 0 }}>
                <Grid container spacing={2.5} alignItems="flex-end">
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2.5}
                      alignItems={{ xs: "flex-start", sm: "flex-end" }}
                      sx={{ mt: -6 }}
                    >
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        badgeContent={
                          <Tooltip title={statusText}>
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                bgcolor: statusColor,
                                border: `3px solid ${ui.surface}`,
                              }}
                            />
                          </Tooltip>
                        }
                      >
                        <Avatar
                          src={staff.image ? `${IMAGE_BASE}${staff.image}` : ""}
                          sx={{
                            width: 116,
                            height: 116,
                            fontSize: "2.4rem",
                            fontWeight: 700,
                            bgcolor: ui.surfaceAlt,
                            color: ui.textPrimary,
                            border: `4px solid ${ui.surface}`,
                            boxShadow: ui.shadow,
                          }}
                        >
                          {initials}
                        </Avatar>
                      </Badge>

                      <Box sx={{ minWidth: 0, pb: 0.5 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.25}
                          sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
                        >
                          <Typography
                            variant="h2"
                            sx={{ fontWeight: 800, color: ui.textPrimary }}
                          >
                            {staff.FirstName} {staff.LastName}
                          </Typography>
                          <Chip
                            size="small"
                            label={statusText}
                            sx={{
                              fontWeight: 700,
                              color: statusColor,
                              bgcolor: alpha(statusColor, 0.14),
                            }}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}
                        >
                          <Chip
                            size="small"
                            icon={<WorkIcon />}
                            label={staff.role || "Staff"}
                            sx={{
                              fontWeight: 700,
                              textTransform: "capitalize",
                              color: ui.accent,
                              bgcolor: alpha(ui.accent, 0.14),
                              "& .MuiChip-icon": { color: "inherit" },
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<SchoolIcon />}
                            label={staff.department || "No department"}
                            sx={{
                              fontWeight: 700,
                              color: colors.indigo[500],
                              bgcolor: alpha(colors.indigo[500], 0.14),
                              "& .MuiChip-icon": { color: "inherit" },
                            }}
                          />
                          {isFlagged(staff.isVerified) && (
                            <Chip
                              size="small"
                              icon={<VerifiedIcon />}
                              label="Verified"
                              sx={{
                                fontWeight: 700,
                                color: colors.green[500],
                                bgcolor: alpha(colors.green[500], 0.14),
                                "& .MuiChip-icon": { color: "inherit" },
                              }}
                            />
                          )}
                        </Stack>

                        <Typography
                          variant="h5"
                          sx={{ color: ui.textSecondary, lineHeight: 1.6, maxWidth: 640 }}
                        >
                          {staff.role_explanation || "No role description provided."}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    >
                      <Tooltip title="Messaging is not available yet">
                        <span>
                          <IconButton disabled sx={actionBtn(ui.accent2)}>
                            <MessageIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit in the team directory">
                        <IconButton
                          onClick={() => navigate("/admin/dashboard/staffs")}
                          sx={actionBtn(colors.orange[500])}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canDelete && (
                        <Tooltip title="Delete from the team directory">
                          <IconButton
                            onClick={() => navigate("/admin/dashboard/staffs")}
                            sx={actionBtn(colors.red[500])}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* ---------------------------------------------------------- */}
            {/* Stat cards                                                  */}
            {/* ---------------------------------------------------------- */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              {statCards.map((card) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      height: "100%",
                      borderRadius: 3,
                      bgcolor: ui.surface,
                      border: `1px solid ${ui.border}`,
                      boxShadow: ui.shadow,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography
                          variant="h1"
                          sx={{
                            fontWeight: 800,
                            lineHeight: 1.05,
                            fontVariantNumeric: "tabular-nums",
                            color: ui.textPrimary,
                          }}
                        >
                          {card.value.toLocaleString()}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            mt: 0.6,
                            letterSpacing: 0.6,
                            textTransform: "uppercase",
                            color: ui.textSecondary,
                          }}
                        >
                          {card.label}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 2,
                          color: card.color,
                          bgcolor: alpha(card.color, isDark ? 0.16 : 0.12),
                        }}
                      >
                        {card.icon}
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* ---------------------------------------------------------- */}
            {/* Tabs                                                        */}
            {/* ---------------------------------------------------------- */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: ui.surface,
                border: `1px solid ${ui.border}`,
                boxShadow: ui.shadow,
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: 1,
                  borderBottom: `1px solid ${ui.border}`,
                  "& .MuiTab-root": {
                    minHeight: 62,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13.5,
                    color: ui.textSecondary,
                    "&.Mui-selected": { color: ui.accent },
                  },
                  "& .MuiTabs-indicator": { backgroundColor: ui.accent, height: 3 },
                }}
              >
                <Tab icon={<PersonIcon />} iconPosition="start" label="Personal info" />
                <Tab icon={<WorkIcon />} iconPosition="start" label="Employment" />
                <Tab
                  icon={<EventAvailableIcon />}
                  iconPosition="start"
                  label={`Leave history (${staffLeaves.length})`}
                />
                <Tab
                  icon={<AssignmentIcon />}
                  iconPosition="start"
                  label={`Borrow history (${staffBorrows.length})`}
                />
                <Tab icon={<TimelineIcon />} iconPosition="start" label="Activity" />
              </Tabs>

              {/* -------- Personal info -------- */}
              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <List disablePadding>
                      <InfoItem
                        icon={<EmailIcon />}
                        color={ui.accent2}
                        label="Email address"
                        value={staff.email}
                        verified={isFlagged(staff.email_verified)}
                      />
                      <InfoItem
                        icon={<PhoneIcon />}
                        color={colors.green[500]}
                        label="Phone number"
                        value={staff.Cell}
                      />
                      <InfoItem
                        icon={<CakeIcon />}
                        color={colors.orange[500]}
                        label="Date of birth"
                        value={dobText(staff)}
                      />
                      <InfoItem
                        icon={<FlagIcon />}
                        color={colors.indigo[500]}
                        label="Nationality"
                        value={staff.nationality}
                      />
                      <InfoItem
                        icon={<BadgeIcon />}
                        color={colors.red[500]}
                        label="National ID"
                        value={staff.national_id}
                      />
                    </List>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <List disablePadding>
                      <InfoItem
                        icon={<LocationOnIcon />}
                        color={ui.accent}
                        label="Physical address"
                        value={addressText(staff.physicalAddress, [
                          "street",
                          "city",
                          "town",
                          "country",
                        ])}
                      />
                      <InfoItem
                        icon={<LocationOnIcon />}
                        color={colors.indigo[500]}
                        label="Postal address"
                        value={addressText(staff.postalAddress, [
                          "code",
                          "city",
                          "town",
                          "country",
                        ])}
                      />
                      <InfoItem
                        icon={<FavoriteIcon />}
                        color={colors.yellow[500]}
                        label="Next of kin"
                        value={nextOfKin?._raw || nextOfKin?.fullname}
                      />
                      <InfoItem
                        icon={<EmailIcon />}
                        color={colors.yellow[500]}
                        label="Next of kin email"
                        value={nextOfKin?.email}
                      />
                      <InfoItem
                        icon={<PhoneIcon />}
                        color={colors.yellow[500]}
                        label="Next of kin cell"
                        value={nextOfKin?.cell}
                      />
                    </List>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* -------- Employment -------- */}
              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <List disablePadding>
                      <InfoItem
                        icon={<WorkIcon />}
                        color={ui.accent}
                        label="Role"
                        value={staff.role}
                      />
                      <InfoItem
                        icon={<SchoolIcon />}
                        color={colors.green[500]}
                        label="Department"
                        value={staff.department}
                      />
                      <InfoItem
                        icon={<BadgeIcon />}
                        color={colors.newTeal[500]}
                        label="Employee type"
                        value={staff.employeeType}
                      />
                    </List>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <List disablePadding>
                      <InfoItem
                        icon={<TodayIcon />}
                        color={colors.orange[500]}
                        label="Start date"
                        value={formatDate(staff.createDate)}
                      />
                      <InfoItem
                        icon={<LoginIcon />}
                        color={colors.indigo[500]}
                        label="Last login"
                        value={formatDateTime(staff.last_login)}
                      />
                      <InfoItem
                        icon={<SecurityIcon />}
                        color={statusColor}
                        label="Employment status"
                        value={statusText}
                      />
                    </List>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Typography
                      variant="h5"
                      sx={{ mt: 1, mb: 2, fontWeight: 700, color: ui.textPrimary }}
                    >
                      About {staff.FirstName}
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { title: "Drive quote", text: staff.drive_quote },
                        { title: "Passion", text: staff.passion },
                        { title: "Knowledge", text: staff.knowledge },
                        { title: "Core values", text: staff.core_values },
                        { title: "Focus", text: staff.focus },
                      ].map((n) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={n.title}>
                          <NarrativeCard title={n.title} text={n.text} />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* -------- Leave history -------- */}
              <TabPanel value={tabValue} index={2}>
                {staffLeaves.length > 0 ? (
                  <List disablePadding>
                    {[...staffLeaves]
                      .sort((a, b) => Number(b.id) - Number(a.id))
                      .map((leave, index, arr) => {
                        const sColor = leaveStatusColor(leave.status);
                        return (
                          <Box key={leave.id}>
                            <ListItem
                              disableGutters
                              sx={{ px: 1, py: 1.5 }}
                              secondaryAction={
                                <Chip
                                  size="small"
                                  label={
                                    LEAVE_STATUS_LABEL[Number(leave.status)] || "Unknown"
                                  }
                                  sx={{
                                    fontWeight: 700,
                                    color: sColor,
                                    bgcolor: alpha(sColor, 0.14),
                                  }}
                                />
                              }
                            >
                              <ListItemAvatar>
                                <Avatar
                                  sx={{
                                    bgcolor: alpha(sColor, isDark ? 0.16 : 0.12),
                                    color: sColor,
                                  }}
                                >
                                  {Number(leave.status) === 2 ||
                                  Number(leave.status) === 4 ? (
                                    <EventAvailableIcon />
                                  ) : Number(leave.status) === 3 ? (
                                    <EventBusyIcon />
                                  ) : (
                                    <ScheduleIcon />
                                  )}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                disableTypography
                                primary={
                                  <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 700, color: ui.textPrimary }}
                                  >
                                    {leave.leave_type} leave
                                  </Typography>
                                }
                                secondary={
                                  <Box sx={{ mt: 0.4 }}>
                                    <Typography
                                      variant="h6"
                                      sx={{ color: ui.textSecondary }}
                                    >
                                      {formatDate(leave.startDate)} —{" "}
                                      {formatDate(leave.endDate)}
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      sx={{ mt: 0.25, color: ui.textSecondary }}
                                    >
                                      {leave.reason || "No reason given"}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                            {index < arr.length - 1 && (
                              <Divider sx={{ borderColor: ui.border }} />
                            )}
                          </Box>
                        );
                      })}
                  </List>
                ) : (
                  <EmptyState icon={<EventBusyIcon fontSize="inherit" />} text="No leave records found" />
                )}
              </TabPanel>

              {/* -------- Borrow history -------- */}
              <TabPanel value={tabValue} index={3}>
                {staffBorrows.length > 0 ? (
                  <List disablePadding>
                    {staffBorrows.map((borrow, index, arr) => {
                      const returned = isFlagged(borrow.status);
                      const bColor = returned ? colors.green[500] : colors.orange[500];
                      return (
                        <Box key={borrow.id}>
                          <ListItem
                            disableGutters
                            sx={{ px: 1, py: 1.5 }}
                            secondaryAction={
                              <Chip
                                size="small"
                                label={returned ? "Returned" : "Outstanding"}
                                sx={{
                                  fontWeight: 700,
                                  color: bColor,
                                  bgcolor: alpha(bColor, 0.14),
                                }}
                              />
                            }
                          >
                            <ListItemAvatar>
                              <Avatar
                                sx={{
                                  bgcolor: alpha(ui.accent2, isDark ? 0.16 : 0.12),
                                  color: ui.accent2,
                                }}
                              >
                                <AssignmentIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              disableTypography
                              primary={
                                <Typography
                                  variant="h5"
                                  sx={{ fontWeight: 700, color: ui.textPrimary }}
                                >
                                  {borrow.equipment || "Unnamed item"}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ mt: 0.4 }}>
                                  <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                                    Borrowed {formatDate(borrow.date)}
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{ mt: 0.25, color: ui.textSecondary }}
                                  >
                                    {[borrow.duration, borrow.destination]
                                      .filter(Boolean)
                                      .join(" · ") || "No further details"}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < arr.length - 1 && (
                            <Divider sx={{ borderColor: ui.border }} />
                          )}
                        </Box>
                      );
                    })}
                  </List>
                ) : (
                  <EmptyState
                    icon={<AssignmentIcon fontSize="inherit" />}
                    text="No borrow records found"
                  />
                )}
              </TabPanel>

              {/* -------- Activity -------- */}
              <TabPanel value={tabValue} index={4}>
                <Typography variant="h6" sx={{ mb: 2, color: ui.textSecondary }}>
                  Built from account, leave and borrow records — not a full audit log.
                </Typography>

                {staffActivity.length > 0 ? (
                  <List disablePadding>
                    {staffActivity.map((activity, index, arr) => {
                      const map = {
                        auth: { icon: <LoginIcon />, color: ui.accent2 },
                        account: { icon: <PersonAddAltIcon />, color: ui.accent },
                        verify: { icon: <VerifiedIcon />, color: colors.green[500] },
                        leave: { icon: <EventAvailableIcon />, color: colors.orange[500] },
                        borrow: { icon: <AssignmentIcon />, color: colors.indigo[500] },
                      };
                      const conf = map[activity.type] || map.account;

                      return (
                        <Box key={activity.key}>
                          <ListItem disableGutters sx={{ px: 1, py: 1.5 }}>
                            <ListItemAvatar>
                              <Avatar
                                sx={{
                                  bgcolor: alpha(conf.color, isDark ? 0.16 : 0.12),
                                  color: conf.color,
                                }}
                              >
                                {conf.icon}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              disableTypography
                              primary={
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{ flexWrap: "wrap", gap: 0.75 }}
                                >
                                  <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 700, color: ui.textPrimary }}
                                  >
                                    {activity.action}
                                  </Typography>
                                  {activity.meta && (
                                    <Chip
                                      size="small"
                                      label={activity.meta}
                                      sx={{
                                        height: 20,
                                        fontSize: "0.68rem",
                                        fontWeight: 700,
                                        color: conf.color,
                                        bgcolor: alpha(conf.color, 0.14),
                                      }}
                                    />
                                  )}
                                </Stack>
                              }
                              secondary={
                                <Typography
                                  variant="h6"
                                  sx={{ mt: 0.35, color: ui.textSecondary }}
                                >
                                  {formatDateTime(activity.date)}
                                </Typography>
                              }
                            />
                          </ListItem>
                          {index < arr.length - 1 && (
                            <Divider sx={{ borderColor: ui.border }} />
                          )}
                        </Box>
                      );
                    })}
                  </List>
                ) : (
                  <EmptyState
                    icon={<TimelineIcon fontSize="inherit" />}
                    text="No activity records found"
                  />
                )}
              </TabPanel>
            </Paper>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default StaffProfile;